/**
 * EmployeeDetailPage: timeline of screenshots + activity log for 1 employee.
 */
import { useMemo, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, CameraOff, ChevronLeft, ChevronRight, X } from 'lucide-react'
import { formatDateTime, formatBytes } from '@/lib/format'
import { resolveScreenshotUrl } from '@/lib/axios'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { buttonVariants } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Spinner } from '@/components/ui/spinner'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/cn'
import { employeeService } from '@/services/employee.service'

const SCREENSHOTS_PAGE_SIZE = 30

// Pad YYYY-MM-DD with the user's local-day boundary for the `to` field so
// picking "từ 2026-08-01 → 2026-08-13" includes the whole day of 13/08, not
// midnight sharp. ISO-stringified to match `DateRangeQuerySchema.from`.
function dayEndIso(date: string): string {
  // date is "YYYY-MM-DD"; end-of-day in local TZ, then convert to UTC ISO.
  const d = new Date(`${date}T23:59:59.999`)
  return d.toISOString()
}
function dayStartIso(date: string): string {
  const d = new Date(`${date}T00:00:00`)
  return d.toISOString()
}

export default function EmployeeDetailPage() {
  const { id } = useParams<{ id: string }>()
  const employeeId = Number(id)
  // Index of the screenshot currently shown in the lightbox.
  // null = lightbox closed.
  const [previewIndex, setPreviewIndex] = useState<number | null>(null)
  // Last index the admin actually viewed (set on click + on each prev/next
  // step). Stays populated after the lightbox closes so the grid tile
  // keeps its highlight ring and the admin can still see "tôi vừa xem
  // tới ảnh này". Cleared only when the admin clicks a different tile
  // — or when the items array itself shifts (page change / new query)
  // so a stale index never highlights the wrong screenshot.
  const [lastPreviewedIndex, setLastPreviewedIndex] = useState<number | null>(null)

  // Pagination & filter state for the screenshot timeline.
  const [page, setPage] = useState(1)
  const [fromDate, setFromDate] = useState<string>('') // "YYYY-MM-DD"
  const [toDate, setToDate] = useState<string>('') // "YYYY-MM-DD"

  const empQuery = useQuery({
    queryKey: ['employee', employeeId],
    queryFn: () => employeeService.get(employeeId),
  })

  const screenshotsQuery = useQuery({
    queryKey: ['employee', employeeId, 'screenshots', page, fromDate, toDate],
    queryFn: () =>
      employeeService.screenshots(employeeId, {
        page,
        pageSize: SCREENSHOTS_PAGE_SIZE,
        from: fromDate ? dayStartIso(fromDate) : undefined,
        to: toDate ? dayEndIso(toDate) : undefined,
      }),
    placeholderData: (prev) => prev,
  })

  const activityQuery = useQuery({
    queryKey: ['employee', employeeId, 'activity'],
    queryFn: () => employeeService.activity(employeeId, { pageSize: 50 }),
    refetchInterval: 30_000,
  })

  const totalScreenshots = screenshotsQuery.data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(totalScreenshots / SCREENSHOTS_PAGE_SIZE))
  const pageRange = useMemo(() => {
    const start = totalScreenshots === 0 ? 0 : (page - 1) * SCREENSHOTS_PAGE_SIZE + 1
    const end = Math.min(page * SCREENSHOTS_PAGE_SIZE, totalScreenshots)
    return { start, end }
  }, [page, totalScreenshots])

  if (empQuery.isLoading) return <Spinner className="m-8 h-6 w-6" />
  if (!empQuery.data) return <div className="p-8">Không tìm thấy nhân viên.</div>
  const emp = empQuery.data

  const clearFilters = () => {
    setFromDate('')
    setToDate('')
    setPage(1)
    // Drop the highlight — the items array will refetch and the old index
    // would otherwise point at a completely different screenshot.
    setPreviewIndex(null)
    setLastPreviewedIndex(null)
  }

  return (
    <div className="container space-y-4 py-4">
      <div className="flex items-center gap-3">
        <Link
          to="/employees"
          aria-label="Quay lại"
          className={buttonVariants({ variant: 'ghost', size: 'icon' })}
        >
          <ArrowLeft className="h-4 w-4" />
        </Link>
        <div>
          <h1 className="text-xl font-semibold">{emp.fullName}</h1>
          <div className="text-sm text-muted-foreground">
            {emp.employeeCode} · {emp.department ?? '—'} · {emp.position ?? '—'}
          </div>
        </div>
        <div className="ml-auto">
          {emp.isActive ? (
            emp.agent?.isOnline ? (
              <Badge variant="success">Online</Badge>
            ) : (
              <Badge variant="secondary">Offline</Badge>
            )
          ) : (
            <Badge variant="destructive">Inactive</Badge>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* Screenshots timeline */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <CardTitle>Ảnh màn hình</CardTitle>
              <div className="text-xs text-muted-foreground">
                {totalScreenshots === 0
                  ? 'Chưa có ảnh'
                  : `Hiển thị ${pageRange.start}–${pageRange.end} / ${totalScreenshots} ảnh`}
              </div>
            </div>
            {/* Date-range filter */}
            <div className="mt-3 flex flex-wrap items-end gap-2">
              <label className="flex flex-col text-xs">
                <span className="mb-1 text-muted-foreground">Từ ngày</span>
                <Input
                  type="date"
                  value={fromDate}
                  max={toDate || undefined}
                  onChange={(e) => {
                    setFromDate(e.target.value)
                    setPage(1)
                  }}
                  className="w-[150px]"
                />
              </label>
              <label className="flex flex-col text-xs">
                <span className="mb-1 text-muted-foreground">Đến ngày</span>
                <Input
                  type="date"
                  value={toDate}
                  min={fromDate || undefined}
                  onChange={(e) => {
                    setToDate(e.target.value)
                    setPage(1)
                  }}
                  className="w-[150px]"
                />
              </label>
              {(fromDate || toDate) && (
                <Button variant="ghost" size="sm" onClick={clearFilters}>
                  Xóa lọc
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {screenshotsQuery.isLoading ? (
              <Spinner />
            ) : (
              <>
                <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
                  {screenshotsQuery.data?.items.map((s, i) => {
                    // Highlight this tile if it's the one currently in
                    // the lightbox OR the one the admin most recently
                    // viewed. Survives lightbox close so the admin can
                    // still see "đây là ảnh tôi vừa zoom xong".
                    const isActive = previewIndex === i || lastPreviewedIndex === i
                    return (
                    <button
                      type="button"
                      key={s.id}
                      onClick={() => {
                        setPreviewIndex(i)
                        setLastPreviewedIndex(i)
                      }}
                      className={cn(
                        'group relative aspect-video overflow-hidden rounded-md border bg-slate-900 hover:ring-2 hover:ring-primary',
                        isActive && 'ring-2 ring-primary ring-offset-2 ring-offset-background',
                      )}
                    >
                      <img
                        src={resolveScreenshotUrl(s.url)}
                        alt={`Screenshot ${s.id}`}
                        className="h-full w-full object-cover"
                        loading="lazy"
                      />
                      {/* Dim non-active tiles so the active one pops.
                          Hover lifts the dim so the admin can preview
                          the screenshot before clicking it. */}
                      {!isActive && (
                        <div
                          aria-hidden
                          className="pointer-events-none absolute inset-0 bg-black/55 transition-opacity group-hover:bg-black/15"
                        />
                      )}
                      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-2 text-[10px] text-white opacity-0 transition-opacity group-hover:opacity-100">
                        {formatDateTime(s.capturedAt)} · {formatBytes(s.fileSize)}
                      </div>
                    </button>
                    )
                  })}
                  {screenshotsQuery.data?.items.length === 0 && (
                    <div className="col-span-full flex flex-col items-center justify-center gap-1.5 py-8 text-sm text-muted-foreground">
                      <CameraOff className="h-6 w-6" aria-hidden />
                      <span>Chưa có ảnh</span>
                    </div>
                  )}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="mt-4 flex items-center justify-between gap-2 border-t pt-3">
                    <div className="text-xs text-muted-foreground">
                      Trang {page} / {totalPages}
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={page <= 1 || screenshotsQuery.isFetching}
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                      >
                        <ChevronLeft className="h-4 w-4" />
                        Trước
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={page >= totalPages || screenshotsQuery.isFetching}
                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      >
                        Sau
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Activity log */}
        <Card>
          <CardHeader>
            <CardTitle>Hoạt động gần đây</CardTitle>
          </CardHeader>
          <CardContent>
            {activityQuery.isLoading ? (
              <Spinner />
            ) : (
              <div className="max-h-[600px] space-y-2 overflow-y-auto text-sm">
                {activityQuery.data?.items.map((a) => (
                  <div key={a.id} className="rounded-md border bg-card p-2">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{a.appName ?? '—'}</span>
                      <span className="text-[10px] text-muted-foreground">{formatDateTime(a.loggedAt)}</span>
                    </div>
                    {a.windowTitle && (
                      <div className="truncate text-xs text-muted-foreground" title={a.windowTitle}>
                        {a.windowTitle}
                      </div>
                    )}
                    {a.url && (
                      <div className="truncate text-xs text-blue-600 dark:text-blue-400" title={a.url}>
                        {a.url}
                      </div>
                    )}
                    {a.isIdle && (
                      <Badge variant="warning" className="mt-1">
                        Idle {a.idleSeconds}s
                      </Badge>
                    )}
                  </div>
                ))}
                {activityQuery.data?.items.length === 0 && (
                  <div className="text-sm text-muted-foreground">Chưa có hoạt động.</div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Lightbox with prev/next nav */}
      {previewIndex !== null && screenshotsQuery.data?.items[previewIndex] && (() => {
        const items = screenshotsQuery.data.items
        const current = items[previewIndex]
        if (!current) return null
        // Each navigation step updates BOTH states: previewIndex drives
        // the lightbox image; lastPreviewedIndex drives the highlight
        // ring on the grid tile so it survives lightbox close. Without
        // updating lastPreviewedIndex here, the ring would snap back to
        // the originally-clicked tile while the user is still browsing.
        const goPrev = () => {
          setPreviewIndex((i) => {
            if (i === null) return null
            const next = (i - 1 + items.length) % items.length
            setLastPreviewedIndex(next)
            return next
          })
        }
        const goNext = () => {
          setPreviewIndex((i) => {
            if (i === null) return null
            const next = (i + 1) % items.length
            setLastPreviewedIndex(next)
            return next
          })
        }
        return (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
            onClick={() => setPreviewIndex(null)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setPreviewIndex(null)
              else if (e.key === 'ArrowLeft') goPrev()
              else if (e.key === 'ArrowRight') goNext()
            }}
            tabIndex={-1}
            ref={(el) => el?.focus()}
          >
            {/* Close button — top right */}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setPreviewIndex(null) }}
              className="absolute right-4 top-4 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-black/60 text-xl text-white shadow-lg backdrop-blur transition-colors hover:bg-black/80"
              aria-label="Đóng"
            >
              <X className="h-5 w-5" />
            </button>

            {/* Counter — top left */}
            <div className="absolute left-4 top-4 z-10 rounded-md bg-black/60 px-3 py-1.5 text-xs text-white shadow backdrop-blur">
              {previewIndex + 1} / {items.length} · {formatDateTime(current.capturedAt)}
            </div>

            {/* Prev button */}
            {items.length > 1 && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); goPrev() }}
                className="absolute left-4 top-1/2 z-10 -translate-y-1/2 flex h-12 w-12 items-center justify-center rounded-full bg-black/60 text-white shadow-lg backdrop-blur transition-colors hover:bg-black/80"
                aria-label="Ảnh trước"
              >
                <ChevronLeft className="h-6 w-6" />
              </button>
            )}

            {/* Image — clicking it does not close */}
            <img
              src={resolveScreenshotUrl(current.url)}
              alt={`Screenshot ${current.id}`}
              className="max-h-full max-w-full"
              onClick={(e) => e.stopPropagation()}
            />

            {/* Next button */}
            {items.length > 1 && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); goNext() }}
                className="absolute right-4 top-1/2 z-10 -translate-y-1/2 flex h-12 w-12 items-center justify-center rounded-full bg-black/60 text-white shadow-lg backdrop-blur transition-colors hover:bg-black/80"
                aria-label="Ảnh sau"
              >
                <ChevronRight className="h-6 w-6" />
              </button>
            )}
          </div>
        )
      })()}
    </div>
  )
}
